// src\stores\resellPipelineStore.js

import { create } from 'zustand';
import { createSelector } from 'reselect';
import {
    getFirestore,
    collection,
    query,
    where,
    onSnapshot,
    doc,
    updateDoc,
    writeBatch,
    deleteDoc,
    getDocs,
    setDoc
} from 'firebase/firestore';
import { notifications } from '@mantine/notifications';
import { getAuth } from 'firebase/auth';

// --- Helpers ---
const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date?.toDate === 'function') return date.toDate();
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
        const d = new Date(date);
        return isNaN(d) ? null : d;
    }
    return null;
};

const toISODateString = (date) => {
    const d = normalizeDate(date);
    return d ? d.toLocaleDateString('sv-SE') : null;
};

/**
 * TECHNICAL DEBT / EMULATION WARNING:
 * This normalizer seeks to emulate the Python backend's `addToSchedule` function.
 * It bridges the gap between the legacy flat-file Excel headers found in the
 * `refresh_candidates` collection and the strict camelCase schema required by
 * the `resell_pipeline_items` collection. Be aware of this mapping if upstream
 * Excel columns change.
 */

const normalizeJITCandidate = (rawItem, currentUserUid) => {
    return {
        originalId: rawItem['Original ID'] || rawItem.originalId || null,
        latestTitle: rawItem['Latest Editorial Title'] || rawItem.latestTitle || '',
        Brand: rawItem['Last CDS Brand'] || rawItem.Brand || '',
        lastFeedId: rawItem['Last Feed ID'] || rawItem.lastFeedId || null,
        lastEntryDate: normalizeDate(rawItem['Last Entry']) || normalizeDate(rawItem.lastEntryDate) || null,
        pageViews: rawItem['Page Views'] || rawItem.pageViews || 0,
        cdsEntries: rawItem['CDS Entries'] || rawItem.cdsEntries || 0,
        refreshScore: rawItem.refreshScore || 0,
        region: rawItem.region,
        language: rawItem.language,
        type: 'Resell',
        pubDate: null,
        pubTime: null,
        addedAt: new Date(),
        addedByUid: currentUserUid
    };
};

const useResellPipelineStore = create((set, get) => ({
    // --- STATE ---
    items: [],
    siblingFamilyItems: [], // <-- ADDED: Holds the panoptic cross-region data
    isFetchingFamily: false, // <-- ADDED: Loading state for the Command Center modal
    feedsMap: {},
    usersMap: {}, // If needed for legacy support
    isLoading: true,
    error: null,

    // Global Filters (still apply to both views)
    region: 'US',
    language: 'EN',
    brand: 'All',

    // --- ACTIONS: FILTERS ---
    setRegion: (region) => {
        set({ region, items: [], isLoading: true, error: null });
        get().initializeSubscriptions();
    },
    setLanguage: (language) => {
        set({ language, items: [], isLoading: true, error: null });
        get().initializeSubscriptions();
    },
    setBrand: (brand) => {
        set({ brand, items: [], isLoading: true, error: null });
        get().initializeSubscriptions();
    },

    // --- ACTIONS: SUBSCRIPTIONS ---
    unsubItems: null,
    unsubFeeds: null,

    initializeSubscriptions: () => {
        const { region, language, brand, unsubItems, unsubFeeds } = get();

        // Clean up previous
        if (unsubItems) unsubItems();
        if (unsubFeeds) unsubFeeds();

        const db = getFirestore();

        // 1. Subscribe to CMS Feeds (Map)
        const feedsCollection = collection(db, 'cms_feeds');
        const newUnsubFeeds = onSnapshot(feedsCollection, (snapshot) => {
            const map = {};
            snapshot.forEach((d) => {
                const feed = d.data();
                const key = `${feed.brand || ''}_${feed.language || ''}_${feed.region || ''}`;
                map[key] = feed.feedId;
            });
            set({ feedsMap: map });
        }, (err) => console.error("Error fetching feeds:", err));

        // 2. Subscribe to Pipeline Items
        // We fetch ALL items for this Region/Language, then filter locally for Prep vs Schedule
        const itemsCollection = collection(db, 'resell_pipeline_items');
        const constraints = [
            where('region', '==', region),
            where('language', '==', language)
        ];
        if (brand !== 'All') constraints.push(where('Brand', '==', brand));

        const qy = query(itemsCollection, ...constraints);

        const newUnsubItems = onSnapshot(qy, (snap) => {
            const arr = [];
            snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
            set({ items: arr, isLoading: false, error: null });
        }, (err) => {
            console.error('Error fetching pipeline items:', err);
            set({
                error: 'Failed to fetch items. Check console/permissions.',
                isLoading: false
            });
        });

        set({ unsubItems: newUnsubItems, unsubFeeds: newUnsubFeeds });
    },

    cleanup: () => {
        const { unsubItems, unsubFeeds } = get();
        if (unsubItems) unsubItems();
        if (unsubFeeds) unsubFeeds();
        set({ unsubItems: null, unsubFeeds: null });
    },

    // --- ACTIONS: FIELD UPDATES ---
    updateField: async (id, field, value) => {
        // Optimistic
        set(state => ({
            items: state.items.map(it => (it.id === id ? { ...it, [field]: value } : it))
        }));
        try {
            const db = getFirestore();
            await updateDoc(doc(db, 'resell_pipeline_items', id), { [field]: value });
        } catch (err) {
            console.error('Update error:', err);
            notifications.show({ title: 'Save Error', message: 'Could not save change.', color: 'red' });
            get().initializeSubscriptions(); // Revert
        }
    },

    bulkUpdate: async (ids, field, value) => {
        // Optimistic
        set(state => ({
            items: state.items.map(it => (ids.includes(it.id) ? { ...it, [field]: value } : it))
        }));
        try {
            const db = getFirestore();
            const batch = writeBatch(db);
            ids.forEach((id) => batch.update(doc(db, 'resell_pipeline_items', id), { [field]: value }));
            await batch.commit();
            notifications.show({ title: 'Success', message: 'Items updated.', color: 'green' });
        } catch (err) {
            console.error('Bulk update error:', err);
            notifications.show({ title: 'Error', message: 'Bulk update failed.', color: 'red' });
            get().initializeSubscriptions();
        }
    },

    // --- NEW ACTIONS: STAGE MANAGEMENT ---

    /**
     * Moves an item from Preparation to Scheduling.
     * Sets the status to 'scheduling'.
     */
    moveToScheduling: async (id) => {
        // Optimistic: Update status locally so it disappears from Prep view immediately
        set(state => ({
            items: state.items.map(it => (it.id === id ? { ...it, status: 'scheduling' } : it))
        }));

        try {
            const db = getFirestore();
            await updateDoc(doc(db, 'resell_pipeline_items', id), { status: 'scheduling' });
            notifications.show({ title: 'Sent to Scheduling', message: 'Item moved to the scheduling queue.', color: 'blue' });
        } catch (err) {
            console.error('Move error:', err);
            notifications.show({ title: 'Error', message: 'Failed to move item.', color: 'red' });
            get().initializeSubscriptions();
        }
    },

    // --- NEW ACTION: Toggle CMS Setup Flag ---
    toggleCMSSetup: async (id, currentValue) => {
        // Optimistic Update
        set(state => ({
            items: state.items.map(it => (it.id === id ? { ...it, isCMSSetup: !currentValue } : it))
        }));

        try {
            const db = getFirestore();
            const docRef = doc(db, 'resell_pipeline_items', id);
            await updateDoc(docRef, { isCMSSetup: !currentValue });
        } catch (err) {
            console.error("Failed to toggle CMS status:", err);
            notifications.show({ title: 'Error', message: 'Could not update status.', color: 'red' });
            get().initializeSubscriptions(); // Revert
        }
    },

    // --- VETO ACTION ---
    vetoItem: async (id, reason) => {
        const { items } = get();
        const originalItems = [...items];

        // 1. Optimistic Update
        set({ items: items.filter((i) => i.id !== id) });

        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) throw new Error("User not authenticated");
            const token = await user.getIdToken();

            const response = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/vetoPipelineItem',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ docId: id, reason: reason })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Veto failed on server');
            }

            // SUCCESS NOTIFICATION ADDED
            notifications.show({ title: 'Blocked', message: 'Item vetoed and removed from pipeline.', color: 'gray' });

        } catch (error) {
            console.error("Veto failed:", error);
            set({ items: originalItems });
            notifications.show({ title: 'Error', message: 'Veto failed. Item restored.', color: 'red' });
        }
    },

    // --- REQUEST UPDATE ACTION ---
    requestUpdate: async (id) => {
        const { items } = get();
        const originalItems = [...items];

        // 1. Optimistic Update
        set({ items: items.filter((i) => i.id !== id) });

        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) throw new Error("User not authenticated");
            const token = await user.getIdToken();

            const response = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/requestUpdateItem',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ docId: id })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Update request failed');
            }

            // SUCCESS NOTIFICATION ADDED
            notifications.show({ title: 'Update Requested', message: 'Item flagged for major update.', color: 'blue' });

        } catch (error) {
            console.error("Update request failed:", error);
            set({ items: originalItems });
            notifications.show({ title: 'Error', message: 'Update request failed. Item restored.', color: 'red' });
        }
    },

    // --- NEW ACTION: REQUEST FAMILY UPDATE ---
    requestFamilyUpdate: async (id, originalId) => {
        const { items } = get();
        const originalItems = [...items];

        // 1. Aggressive Optimistic Update: Sweep all siblings from UI instantly
        set({ items: items.filter((i) => i.originalId !== originalId) });

        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) throw new Error("User not authenticated");
            const token = await user.getIdToken();

            const response = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/requestUpdateFamily',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ docId: id })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Family update request failed');
            }

            notifications.show({
                title: 'Family Update Requested',
                message: 'All sibling variants flagged for major update.',
                color: 'blue'
            });

        } catch (error) {
            console.error("Family update request failed:", error);
            // Revert state on failure
            set({ items: originalItems });
            notifications.show({
                title: 'Error',
                message: 'Family update failed. Items restored.',
                color: 'red'
            });
        }
    },

    deleteItem: async (id, silent = false) => {
        set(state => ({ items: state.items.filter(it => it.id !== id) }));
        try {
            const db = getFirestore();
            await deleteDoc(doc(db, 'resell_pipeline_items', id));

            // Only show notification if NOT silent
            if (!silent) {
                notifications.show({ title: 'Removed', message: 'Item deleted.', color: 'green' });
            }
        } catch (err) {
            get().initializeSubscriptions();
            if (!silent) {
                notifications.show({ title: 'Error', message: 'Could not delete item.', color: 'red' });
            }
        }
    },

    // --- NEW ACTION: BULK DELETE ---
    bulkDelete: async (ids) => {
        // 1. Optimistic Update: Remove items from local state immediately
        set(state => ({
            items: state.items.filter(it => !ids.includes(it.id))
        }));

        try {
            const db = getFirestore();
            const batch = writeBatch(db);

            // 2. Queue up deletes in a batch
            ids.forEach(id => {
                const docRef = doc(db, 'resell_pipeline_items', id);
                batch.delete(docRef);
            });

            // 3. Commit the batch
            await batch.commit();
            notifications.show({ title: 'Bulk Delete', message: `${ids.length} items permanently removed.`, color: 'green' });
        } catch (err) {
            console.error("Bulk delete failed:", err);
            notifications.show({ title: 'Error', message: 'Bulk delete failed.', color: 'red' });
            get().initializeSubscriptions(); // Revert on error
        }
    },

    // --- NEW ACTION: REJECT / PUSH BACK ---
    rejectToPreparation: async (id) => {
        // Optimistic Update
        set(state => ({
            items: state.items.map(it => (it.id === id ? {
                ...it,
                status: 'Staging', // <--- Explicitly set to Staging
                pubDate: null,
                pubTime: null
            } : it))
        }));

        try {
            const db = getFirestore();
            await updateDoc(doc(db, 'resell_pipeline_items', id), {
                status: 'Staging', // <--- Write to Firestore
                pubDate: null,
                pubTime: null
            });
            notifications.show({ title: 'Returned', message: 'Item returned to Preparation stage.', color: 'blue' });
        } catch (err) {
            console.error("Reject failed:", err);
            notifications.show({ title: 'Error', message: 'Could not push item back.', color: 'red' });
            get().initializeSubscriptions(); // Revert
        }
    },


    // --- MULTI-REGION COMMAND CENTER ACTIONS ---

    fetchSiblingFamily: async (originalId) => {
        if (!originalId) return;
        set({ isFetchingFamily: true, siblingFamilyItems: [] });

        try {
            const db = getFirestore();

            // 1. Fire Parallel Queries (The Fan-Out)
            const queries = [
                getDocs(query(collection(db, 'refresh_candidates'), where('Original ID', '==', originalId))),
                getDocs(query(collection(db, 'resell_veto_log'), where('originalId', '==', originalId))),
                getDocs(query(collection(db, 'resell_update_queue'), where('originalId', '==', originalId))),
                getDocs(query(collection(db, 'resell_complete_items'), where('originalId', '==', originalId))),
                getDocs(query(collection(db, 'resell_pipeline_items'), where('originalId', '==', originalId)))
            ];

            const [
                candidatesSnap,
                vetoSnap,
                updateSnap,
                completeSnap,
                pipelineSnap
            ] = await Promise.all(queries);

            // 2. The Reducer Map (Dictionary)
            const familyMap = new Map();

            // Helper to process and prioritize variants
            const processDoc = (docSnap, basePriority, overrides = {}) => {
                const data = { id: docSnap.id, ...docSnap.data(), ...overrides };
                const variantKey = `${data.region || ''}_${data.language || ''}`;

                // Determine exact priority (P4 vs P5 logic for pipeline items)
                let finalPriority = basePriority;
                if (basePriority === 5 && data.pubDate) {
                    finalPriority = 4; // It's scheduled, downgrade to Read-Only
                }

                const existing = familyMap.get(variantKey);

                // Overwrite if it doesn't exist, or if the new priority is strictly higher
                if (!existing || finalPriority > existing._priority) {
                    familyMap.set(variantKey, { ...data, _priority: finalPriority });
                }
            };

            // 3. Map Results according to the Priority Ladder (Ascending)
            candidatesSnap.forEach(d => processDoc(d, 1)); // Priority 1: Upstream
            vetoSnap.forEach(d => processDoc(d, 2));       // Priority 2: Vetoed
            updateSnap.forEach(d => processDoc(d, 3));     // Priority 3: Update Queue
            completeSnap.forEach(d => processDoc(d, 4));   // Priority 4: Complete
            pipelineSnap.forEach(d => processDoc(d, 5));   // Priority 4/5: Active Pipeline

            // 4. Finalize and push to UI state
            const finalizedItems = Array.from(familyMap.values());
            set({ siblingFamilyItems: finalizedItems, isFetchingFamily: false });

        } catch (error) {
            console.error("Error fetching sibling family:", error);
            notifications.show({ title: 'Data Error', message: 'Failed to load sibling variants.', color: 'red' });
            set({ isFetchingFamily: false });
        }
    },

    executeFamilyUpdates: async (payloadItems) => {
        try {
            const db = getFirestore();
            const batch = writeBatch(db);
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) throw new Error("Authentication required for batch updates.");

            payloadItems.forEach(item => {
                // Ignore items that aren't P1 or P5, or lack a title (UI should catch this, but safeguard)
                if (!item.newTitle || (item._priority !== 1 && item._priority !== 5)) return;

                const updateData = {
                    newTitle: item.newTitle,
                    newLeadImageSlideNo: item.newLeadImageSlideNo || null,
                    status: 'scheduling' // Force state transition
                };

                if (item._priority === 5) {
                    // Bucket A: Standard Update (Item is already in pipeline)
                    const docRef = doc(db, 'resell_pipeline_items', item.id);
                    batch.update(docRef, updateData);
                }
                else if (item._priority === 1) {
                    // Bucket B: JIT Promotion (Item is moving from upstream into pipeline)
                    const docRef = doc(db, 'resell_pipeline_items', item.id); // Re-use the upstream ID for continuity

                    // 1. Emulate addToSchedule to sanitize the raw upstream document
                    const sanitizedBase = normalizeJITCandidate(item, currentUser.uid);

                    // 2. Merge with the explicit UI overrides (Title, Image Slide, Status)
                    const newPipelineDoc = {
                        ...sanitizedBase,
                        ...updateData
                    };

                    batch.set(docRef, newPipelineDoc);
                }
            });

            await batch.commit();
            notifications.show({ title: 'Success', message: 'Variants successfully scheduled.', color: 'green' });

            // Clear the modal state upon success
            set({ siblingFamilyItems: [] });

        } catch (error) {
            console.error("Batch update failed:", error);
            notifications.show({ title: 'Save Error', message: 'Failed to process bulk updates.', color: 'red' });
            throw error; // Re-throw so the UI knows to halt loading states
        }
    },

    // Archive (Existing Logic)
    archiveItem: async (id) => {
        set(state => ({ items: state.items.filter(it => it.id !== id) }));
        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();
            const res = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/archiveItem',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ docId: id }),
                }
            );
            if (!res.ok) throw new Error('Failed to archive.');
            notifications.show({ title: 'Archived', message: 'Item moved to archive.', color: 'blue' });
        } catch (err) {
            get().initializeSubscriptions();
        }
    }
}));

// --- SELECTORS ---

const selectItems = (state) => state.items;

/**
 * SELECTOR: Preparation View
 * Logic: Items that are NOT 'scheduling' AND have NO date set.
 * This captures: 
 * 1. New items from Radar (status undefined or 'Staging').
 * 2. Items editor hasn't finished with.
 */
export const selectPreparationItems = createSelector(
    [selectItems],
    (items) => {
        return items.filter(item => {
            const isScheduledStatus = item.status === 'scheduling';
            const hasDate = !!item.pubDate; // Non-null date implies it was already worked on
            return !isScheduledStatus && !hasDate;
        }).sort((a, b) => (b.refreshScore || 0) - (a.refreshScore || 0)); // Sort by Score desc
    }
);

/**
 * SELECTOR: Scheduling View
 * Logic: Items that ARE 'scheduling' OR have a Date set.
 * This captures:
 * 1. Items sent by Editor (status='scheduling').
 * 2. Legacy items that already had dates.
 */
export const selectSchedulingItems = createSelector(
    [selectItems],
    (items) => {
        return items.filter(item => {
            const isScheduledStatus = item.status === 'scheduling';
            const hasDate = !!item.pubDate;
            return isScheduledStatus || hasDate;
        }).sort((a, b) => {
            // Sort by Date (ascending) so earliest slots are first
            const dateA = normalizeDate(a.pubDate);
            const dateB = normalizeDate(b.pubDate);
            if (!dateA && !dateB) return 0;
            if (!dateA) return -1; // Undated (Inbox) at top
            if (!dateB) return 1;
            return dateA - dateB;
        });
    }
);

export default useResellPipelineStore;

// --- LEGACY SUPPORT (Restoring for ResellPipelinePageZustand.js) ---
const selectShowHistory = (state) => state.showHistory || false;
const selectShowUnscheduledOnly = (state) => state.showUnscheduledOnly || false;

export const getFilteredAndSortedItems = createSelector(
    [selectItems, selectShowHistory, selectShowUnscheduledOnly],
    (items, showHistory, showUnscheduledOnly) => {
        const todayStr = toISODateString(new Date());

        const initial = items.filter((item) => {
            if (showHistory) return true;
            const pubDateStr = toISODateString(item.pubDate);
            return !pubDateStr || pubDateStr >= todayStr;
        });

        const filtered = showUnscheduledOnly ? initial.filter((i) => !i.pubDate) : initial;

        return [...filtered].sort((a, b) => {
            const dateA = normalizeDate(a.pubDate);
            const dateB = normalizeDate(b.pubDate);
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            const d = dateA - dateB;
            if (d !== 0) return d;
            const timeA = a.pubTime || '00:00';
            const timeB = b.pubTime || '00:00';
            return timeA.localeCompare(timeB);
        });
    }
);